import React, { useRef, useState, useEffect } from 'react'
import { Spinner } from '@chakra-ui/react'
import { NetworkStatus } from '@apollo/client'
import { Center, Flex } from '@chakra-ui/react'
import type { FlexProps } from '@chakra-ui/react'
import { useColorModeValue } from '@chakra-ui/react'

interface Props extends FlexProps {
    fetchMore: () => Promise<void>
    networkStatus: NetworkStatus
    hasMore: boolean
    inverted?: boolean
}

export default function FetchMoreWrapper({
    fetchMore,
    networkStatus,
    hasMore,
    inverted,
    children,
    ...rest
}: Props) {
    const wrapperRef = useRef<HTMLDivElement>(null)
    const markerRef = useRef<HTMLDivElement>(null)

    const [shouldFetch, setShouldFetch] = useState(false)
    const emptyColor = useColorModeValue('gray.200', 'gray.700')

    useEffect(() => {
        if (shouldFetch && networkStatus === 7 && hasMore) {
            fetchMore().then(() => {
                setShouldFetch(false)
            })
        }
    }, [shouldFetch, networkStatus, hasMore])

    useEffect(() => {
        function handleScroll() {
            const isAtTop = markerRef.current?.offsetTop
                ? Math.abs(
                    markerRef.current?.offsetTop -
                    (wrapperRef.current?.scrollTop || 0),
                ) < 5
                : false
            setShouldFetch(isAtTop)
        }

        wrapperRef.current?.addEventListener('scroll', handleScroll)

        return () => {
            wrapperRef.current?.removeEventListener('scroll', handleScroll)
        }
    }, [])

    return (
        <Flex
            ref={wrapperRef}
            w="full"
            overflowY="auto"
            overflowX="hidden"
            position="relative"
            overflowWrap="anywhere"
            flexDir={inverted ? 'column-reverse' : 'column'}
            {...rest}
        >
            {children}
            {hasMore && (
                <Center py={4} ref={markerRef}>
                    <Spinner
                        color="brand.500"
                        size="md"
                        thickness="3px"
                        emptyColor={emptyColor}
                    />
                </Center>
            )}
        </Flex>
    )
}
